import * as React from "react";
import { toast } from "react-toastify";

import { ApplicantExamModel, ApplicantExamTimerModel } from "@shared/models";
import { dayjs } from "@shared/services/date";
import {
  emitter,
  MESSAGE_EVENT_CONTENT_CHANGED,
  MESSAGE_EVENT_SAVED,
  MessageSaved,
} from "@shared/services/events";
import { getMillisBeforeExamDeadline } from "@shared/services/exam";
import {
  KEY_EVENT_ERROR_DUPLICATE_CONNECTION,
  KeyEventError,
  KeyEventService,
} from "@shared/services/keyEvents";
import Message from "@shared/services/message";

export type ExternalProps = {};

export type InjectedProps = {
  keyEventToken: string | undefined;
  challengeStorageId: string | undefined;
  challengeResultHistoryId: number | undefined;
  extensionId?: string;
  exam: ApplicantExamModel;
  timer: ApplicantExamTimerModel;
  sessionDuplicate: () => void;
};

export type KeyEventHandlerProps = ExternalProps & InjectedProps;

export const KeyEventHandler = (props: KeyEventHandlerProps) => {
  const {
    keyEventToken,
    challengeStorageId,
    challengeResultHistoryId,
    extensionId,
    timer,
    exam,
    sessionDuplicate,
  } = props;

  const keyEventServiceRef = React.useRef<KeyEventService>();
  const handleLoadEventRef = React.useRef<() => void>();

  const getTimestamp = React.useCallback(() => {
    const millisBeforeExamDeadline = getMillisBeforeExamDeadline(exam, timer);
    const timestamp = dayjs(exam.deadline)
      .add(-millisBeforeExamDeadline, "ms")
      .toISOString();
    return { millisBeforeExamDeadline, timestamp };
  }, [timer, exam]);

  const handleLoadEvent = React.useCallback(() => {
    const { millisBeforeExamDeadline, timestamp } = getTimestamp();
    keyEventServiceRef.current?.send(
      { challengeResultHistoryId, type: "load" },
      timestamp,
      millisBeforeExamDeadline,
    );
  }, [getTimestamp, challengeResultHistoryId]);

  const handleKeyEvent = React.useCallback(
    (data: {}) => {
      const { millisBeforeExamDeadline, timestamp } = getTimestamp();
      keyEventServiceRef.current?.send(
        { ...data, type: "key" },
        timestamp,
        millisBeforeExamDeadline,
      );
    },
    [getTimestamp],
  );

  const handleSaveEvent = React.useCallback(
    (data: MessageSaved) => {
      const { challengeResultHistoryId } = data;
      const { millisBeforeExamDeadline, timestamp } = getTimestamp();
      // don't send save event when challengeVersionId is undefined
      // because it is not changed anything
      if (challengeResultHistoryId !== undefined) {
        keyEventServiceRef.current?.send(
          { challengeResultHistoryId, type: "save" },
          timestamp,
          millisBeforeExamDeadline,
        );
      }
    },
    [getTimestamp],
  );

  const handleError = React.useCallback(
    (e: KeyEventError) => {
      const { code } = e;
      if (code === KEY_EVENT_ERROR_DUPLICATE_CONNECTION) {
        sessionDuplicate();
      }
      toast.error(e.message);
    },
    [sessionDuplicate],
  );

  const handleConnectError = React.useCallback((e: Error) => {
    toast.error(Message.getMessageByKey("message.connectionLost"));
  }, []);

  const handleShutdown = React.useCallback(() => {
    keyEventServiceRef.current?.reconnect();
  }, []);

  React.useEffect(() => {
    handleLoadEventRef.current = handleLoadEvent;
  }, [handleLoadEvent]);

  React.useEffect(() => {
    if (challengeStorageId == undefined || keyEventToken == undefined) {
      return;
    }

    const initialize = async () => {
      keyEventServiceRef.current = new KeyEventService(
        await KeyEventService.connect({
          keyEventToken,
          challengeResultStorageId: challengeStorageId,
          extensionId,
          onShutdown: handleShutdown,
          onError: handleError,
          onConnectError: handleConnectError,
        }),
      );
      handleLoadEventRef.current?.();
    };

    initialize();

    return () => {
      keyEventServiceRef.current?.destroy();
      keyEventServiceRef.current = undefined;
    };
  }, [
    keyEventToken,
    challengeStorageId,
    extensionId,
    handleError,
    handleConnectError,
    handleShutdown,
  ]);

  React.useEffect(() => {
    emitter.addListener(MESSAGE_EVENT_CONTENT_CHANGED, handleKeyEvent);
    emitter.addListener(MESSAGE_EVENT_SAVED, handleSaveEvent);

    return () => {
      emitter.removeAllListeners(MESSAGE_EVENT_CONTENT_CHANGED);
      emitter.removeAllListeners(MESSAGE_EVENT_SAVED);
    };
  }, [handleKeyEvent, handleSaveEvent]);

  return null;
};
